import React, { useState, useEffect } from 'react';
import { PencilIcon, EyeIcon, CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient, PromptTypesResponse, BaseTemplate } from '../lib/api';
import ChatModelSettings from '../components/ChatModelSettings';

const AdvancedSettings: React.FC = () => {
  const [templates, setTemplates] = useState<BaseTemplate[]>([]);
  const [promptTypes, setPromptTypes] = useState<PromptTypesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [previewContent, setPreviewContent] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewGoal, setPreviewGoal] = useState<string>('assist you');
  const [saving, setSaving] = useState<boolean>(false);

  // Load templates and types on component mount
  useEffect(() => {
    loadTemplates();
    loadPromptTypes();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getBaseTemplates();

      // Handle response structure - API returns {prompts: [...], total: number}
      if (response && Array.isArray(response.prompts)) {
        setTemplates(response.prompts);
      } else if (Array.isArray(response)) {
        // Fallback in case API structure changes back to direct array
        setTemplates(response);
      } else {
        console.error('Expected array or object with prompts property but received:', typeof response, response);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading base templates:', error);
      setTemplates([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadPromptTypes = async () => {
    try {
      const response = await apiClient.getPromptTypes();

      // Handle simplified response structure (no heat levels)
      if (response && Array.isArray(response.prompt_types)) {
        setPromptTypes(response);
      } else {
        console.error('Invalid prompt types response structure:', response);
        setPromptTypes(null);
      }
    } catch (error) {
      console.error('Error loading prompt types:', error);
      setPromptTypes(null);
    }
  };

  const getCurrentTemplate = (): BaseTemplate | undefined => {
    // Safety check to ensure templates is an array before calling .find()
    if (!Array.isArray(templates)) {
      console.error('Templates is not an array:', templates);
      return undefined;
    }

    // Since we only have GENERAL type now, just find the first (and only) template
    return templates.find(t => t.prompt_type === 'general');
  };

  const handleEdit = (template: BaseTemplate) => {
    setEditingTemplate(template.prompt_type);
    setEditContent(template.template);
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      setSaving(true);
      // Updated API call to use new simplified endpoint
      await apiClient.updateBaseTemplate(editingTemplate, {
        template: editContent
      });

      // Reload templates to get updated data
      await loadTemplates();
      setEditingTemplate(null);
      setEditContent('');
    } catch (error) {
      console.error('Error saving base template:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setEditContent('');
  };

  const handlePreview = async () => {
    try {
      // Get the template content to preview
      const templateContent = editingTemplate ? editContent : (currentTemplate?.template || '');

      if (!templateContent) {
        console.error('No template content available for preview');
        return;
      }

      const response = await apiClient.previewBasePrompt({
        template: templateContent,
        variables: {
          goal: previewGoal,
          current_heat_category_name: 'general' // Just use general as placeholder
        }
      });

      setPreviewContent(response.rendered_content || '');
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  const currentTemplate = getCurrentTemplate();
  const isEditing = editingTemplate === 'general';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!promptTypes) {
    return (
      <div className="text-center text-gray-500">
        Unable to load prompt configuration
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation */}
      <div className="mb-4">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Settings
        </Link>
        <nav className="flex mt-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/settings" className="text-gray-500 hover:text-gray-700">
                Settings
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-gray-500 md:ml-2">Advanced Settings</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Advanced Settings</h1>
        <p className="text-gray-600 mt-2">
          Choose the AI chat model and manage the unified base AI prompt template that serves as the foundation for all tenant customizations.
        </p>
      </div>

      <ChatModelSettings />

      {/* Template Editor */}
      {currentTemplate ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  General AI Assistant Template
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Version {currentTemplate.version} • This unified template handles all conversation types: greetings, products, services, and documents
                </p>
              </div>
              <div className="flex space-x-2">
                {!isEditing && (
                  <>
                    <button
                      onClick={() => handleEdit(currentTemplate)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={handlePreview}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Preview
                    </button>
                  </>
                )}
                {isEditing && (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <XIcon className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Template Content
                  </label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={20}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="Enter the base template content..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: {'{goal}'}
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preview Goal
                    </label>
                    <input
                      type="text"
                      value={previewGoal}
                      onChange={(e) => setPreviewGoal(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Enter a goal for preview..."
                    />
                  </div>
                  <button
                    onClick={handlePreview}
                    className="mt-6 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Preview Changes
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Base Template Content
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                    {currentTemplate.template}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center text-gray-500">
            <p className="text-lg font-medium">No template found</p>
            <p className="text-sm mt-1">
              No base template exists for the general AI assistant
            </p>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Base Template Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                  {previewContent}
                </pre>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettings;
